import type { ReactNode } from 'react';
import { use } from 'react';

import { SignInPage } from '@/pages/SignInPage';
import { AuthContext } from './AuthContext';

interface AuthGateProps {
    children: ReactNode;
}

/**
 * Guards protected application content behind authentication.
 *
 * Renders one of three states:
 *   1. Loading  — MSAL is processing a redirect or in-flight interaction.
 *                 Shows a full-screen spinner to prevent content flicker.
 *   2. Sign-in  — No authenticated session. Renders the sign-in page.
 *   3. Authed   — Valid session exists. Renders the protected app children.
 *
 * Must be rendered inside an <AuthProvider>.
 */
export function AuthGate({ children }: AuthGateProps) {
    const auth = use(AuthContext);

    if (!auth) {
        throw new Error('<AuthGate> must be rendered inside an <AuthProvider>');
    }

    if (auth.isLoading) {
        return (
            <div
                className="flex min-h-screen items-center justify-center bg-black"
                role="status"
                aria-label="Signing in…"
            >
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-white/20 border-t-white" />
            </div>
        );
    }

    if (!auth.isAuthenticated) {
        return <SignInPage />;
    }

    return <>{children}</>;
}
